import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Alert, AlertColor, Snackbar } from '@mui/material';

export type NotifyFn = (message: string, severity?: AlertColor) => void;

const NotificationContext = createContext<NotifyFn>(() => {
    // eslint-disable-next-line no-console
    console.warn('useNotification() called outside of a NotificationProvider');
});

// Central replacement for the browser's native alert() - call `notify(message, severity)` from
// anywhere in the tree instead. Renders as a single MUI Snackbar+Alert mounted once at the app
// root, so callers don't each need their own Snackbar state/markup.
export function useNotification(): NotifyFn {
    return useContext(NotificationContext);
}

type NotificationState = {
    open: boolean;
    message: string;
    severity: AlertColor;
};

const AUTO_HIDE_MS = 6000;

export function NotificationProvider({ children }: { children: React.ReactNode }) {
    const [state, setState] = useState<NotificationState>({ open: false, message: '', severity: 'info' });

    const notify = useCallback<NotifyFn>((message, severity = 'info') => {
        setState({ open: true, message, severity });
    }, []);

    const handleClose = useCallback((_event?: React.SyntheticEvent | Event, reason?: string) => {
        if (reason === 'clickaway') return;
        setState((prev) => ({ ...prev, open: false }));
    }, []);

    const value = useMemo(() => notify, [notify]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
            <Snackbar
                open={state.open}
                autoHideDuration={AUTO_HIDE_MS}
                onClose={handleClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert onClose={handleClose} severity={state.severity} variant="filled" sx={{ width: '100%', boxShadow: 3 }}>
                    {state.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
}
